import React, { useState } from "react";
import { Info, Copy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/components/ui/use-toast";

interface Chat8SessionInfoProps {
  sessionId: string;
  documentId: string;
  defaultExpanded?: boolean;
}

export const Chat8SessionInfo: React.FC<Chat8SessionInfoProps> = ({
  sessionId,
  documentId,
  defaultExpanded = false,
}) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const { toast } = useToast();

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Copiado!",
        description: `${label} copiado para a área de transferência`,
      });
    } catch (err) {
      toast({
        title: "Erro",
        description: "Não foi possível copiar",
        variant: "destructive",
      });
    }
  };

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="w-full"
    >
      <div className="glass-card rounded-lg border border-white/10 overflow-hidden">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-between p-4 hover:bg-white/5"
          >
            <div className="flex items-center gap-2">
              <Info className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-semibold text-platinum">
                Informações da Sessão
              </span>
            </div>
            <Badge variant="outline" className="bg-blue-500/20 text-blue-300 border-blue-500/30">
              {isOpen ? "Ocultar" : "Mostrar"}
            </Badge>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="p-4 space-y-3 border-t border-white/10">
            {/* Session ID */}
            <div className="flex items-center gap-2 p-3 bg-navy-700/50 rounded-lg border border-white/10">
              <div className="flex-1 space-y-1">
                <p className="text-xs font-medium text-silver">Sessão ID</p>
                <p className="text-xs font-mono text-platinum/80 break-all">
                  {sessionId}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handleCopy(sessionId, "ID da sessão")}
                className="shrink-0 h-8 w-8 hover:bg-white/10"
              >
                <Copy className="w-4 h-4 text-silver" />
              </Button>
            </div>
            {/* Document ID */}
            <div className="flex items-center gap-2 p-3 bg-navy-700/50 rounded-lg border border-white/10">
              <div className="flex-1 space-y-1">
                <p className="text-xs font-medium text-silver">Documento ID</p>
                <p className="text-xs font-mono text-platinum/80 break-all">
                  {documentId}
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => handleCopy(documentId, "ID do documento")}
                className="shrink-0 h-8 w-8 hover:bg-white/10"
              >
                <Copy className="w-4 h-4 text-silver" />
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
